import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useController } from 'react-hook-form';
import { useMemo } from 'react';
import classnames from 'classnames';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { FormHelperText } from '@mui/material';

import { AlxTextField, themeColors } from '../../alx-components/input';
import { RootState } from '../../../store/rootReducer';
import { theme } from '../../../global-styles/colors';
import { UseControllerProps } from './forms-types';

const AutoCompleteStyles = styled.div`
  width: 100%;
  .MuiFormControl-marginNormal {
    margin-bottom: 0;
  }
  .autocomplete-error {
    label {
      color: ${theme.status.error.medium};
    }
  }
  && .MuiFilledInput-root {
    border: 1px solid #bfc4ca !important;
    background: white !important;
    border-radius: 4px;
    &:hover {
      background: white;
      border: 1px solid var(--color);
    }
    &.Mui-focused {
      background: white;
      border: 1px solid #0094d9 !important;
    }
  }
  && .MuiFilledInput-underline:after,
  && .MuiFilledInput-underline:before {
    display: none;
  }
  && .MuiFormLabel-root {
    color: #bfc4ca;
    &.Mui-focused {
      color: var(--focus);
    }
  }
`;

export interface IAutoCompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Partial<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>> {
  options: T[];
  'data-cy'?: string;
  label?: string;
  controllerProps: UseControllerProps;
  helperText?: string;
  error?: boolean;
  formatOnChange?: (newValue: any) => any;
}

function ControlledAutoComplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(props: Readonly<IAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>>) {
  const {
    controllerProps,
    label,
    helperText,
    error,
    className,
    formatOnChange,
    'data-cy': dataCy,
    ...restOfProps
  } = props;

  const {
    field: { onChange, ref, ...fieldProps },
  } = useController({
    ...controllerProps,
  });

  const providerCode = useSelector((state: RootState) => state.general.provider);

  const themeInputs = useMemo(() => {
    if (providerCode) {
      return themeColors[providerCode as keyof typeof themeColors];
    }
    return themeColors['00000016'];
  }, [providerCode]);

  return (
    <AutoCompleteStyles data-cy={dataCy} style={themeInputs}>
      <Autocomplete
        {...restOfProps}
        {...fieldProps}
        ref={ref}
        className={classnames(className, {
          'autocomplete-error': !!error,
        })}
        renderInput={(params: any) => <AlxTextField {...params} label={label} margin="normal" variant="filled" />}
        {...(formatOnChange ? { onChange: (_: any, option: any) => onChange(formatOnChange(option)) } : {})}
      />
      {helperText && (
        <FormHelperText className="MuiFormHelperText-contained" error={error} component="p">
          {helperText}
        </FormHelperText>
      )}
    </AutoCompleteStyles>
  );
}

export default ControlledAutoComplete;
